import API from "../api"; // resolves to src/api.js
import { fetchAllPages } from "./fetchAllPages";

export const listManagers = async ({
  active = true,
  page = 0,
  size = 100,
} = {}) => {
  const res = await API.get("/admin/users", {
    params: { role: "MANAGER", active, page, size },
  });
  // Support either array or page wrapper shape
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

// Add this to src/api/users.js
export const listStudents = async ({
  active = true,
  q = "",
  page = 0,
  size = 100,
} = {}) => {
  const res = await API.get("/admin/users", {
    params: { role: "STUDENT", active, q, page, size },
  });
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

/** All active students via paginated search (safe for large lists). */
export const listAllActiveStudents = async () =>
  fetchAllPages(
    ({ page, size }) =>
      API.get("/admin/users/students/search", {
        params: { q: "", status: "active", page, size },
      }),
    200,
  );

/** All students (active + archived) for admin flows that must include inactive accounts. */
export const listAllStudents = async () =>
  fetchAllPages(
    ({ page, size }) =>
      API.get("/admin/users/students/search", {
        params: { q: "", status: "all", page, size },
      }),
    200,
  );

/** Active students visible to the current manager (branch-scoped). */
export const listManagerActiveStudents = async () => {
  const rows = await fetchAllPages(
    ({ page, size }) =>
      API.get("/manager/users", { params: { role: "STUDENT", page, size } }),
    200,
  );
  return (rows || []).filter((s) => s.active !== false);
};

/** All students visible to the current manager, including archived. */
export const listManagerAllStudents = async () =>
  fetchAllPages(
    ({ page, size }) =>
      API.get("/manager/users", { params: { role: "STUDENT", page, size } }),
    200,
  );

export const listAdmins = async ({
  active = true,
  q = "",
  page = 0,
  size = 100,
} = {}) => {
  const res = await API.get("/admin/users", {
    params: { role: "ADMIN", active, q, page, size },
  });
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

/** Students on the current coach's roster (sessions + admin assignments). */
export const listCoachRosterStudents = async () => {
  const res = await API.get("/coach/students");
  const data = res?.data;
  return Array.isArray(data) ? data : [];
};

export const listCoaches = async ({
  active = true,
  q = "",
  page = 0,
  size = 100,
} = {}) => {
  const res = await API.get("/admin/users", {
    params: { role: "COACH", active, q, page, size },
  });
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

export const listStaff = async ({
  active = true,
  q = "",
  page = 0,
  size = 100,
} = {}) => {
  const res = await API.get("/admin/users/staff");
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

export const getUsers = async () => {
  const res = await API.get("/admin/users", {
    params: { size: 1000 },
  });
  const data = res?.data;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : [];
  return items;
};

/** Soft-delete: sets user inactive (archived). Blocked for students with active enrollments — use guided flow. */
export const archiveUser = (id) => API.delete(`/admin/users/${id}`);

export const checkStudentArchive = (id) => API.get(`/admin/users/${id}/archive-check`);

export const archiveStudentGuided = (id, body) =>
  API.post(`/admin/users/${id}/archive-guided`, body);

/** Restore sign-in access for an archived user. */
export const activateUser = (id) => API.patch(`/admin/users/${id}/activate`);

/** Soft permanent removal: hide from lists, free license slot, keep history as (Inactive). */
export const permanentlyRemoveUser = (id) =>
  API.post(`/admin/users/${id}/permanent-remove`);

/** Email a link so the user can set or reset their password. */
export const resendUserInvite = (id) => API.post(`/admin/users/${id}/invite`);
